import {
  Box,
  Flex,
  Divider,
  Center,
  useToast,
  Progress,
  Button,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { error as commonError } from '@finley-technologies/common';

import { DisplayNode } from '../hooks/bbplMonitorContext';
import { CashBalanceUploadButton } from './CashBalanceUploadButton';
import { COLORS, SIZES } from './consts';
import { FileUploadButton } from './FileUploadButton';
import { NodeHeader } from './NodeHeader';
import { NodeStatus } from './NodeStatusIndicator';
import { FiUpload } from 'react-icons/fi';

type UploadProgress = {
  loaded: number;
  total: number;
};

export const NodeDisplay = ({ node }: { node: DisplayNode }) => {
  const toast = useToast();
  const [
    uploadProgress,
    setUploadProgress,
  ] = React.useState<UploadProgress | null>(null);

  const [displayId, setDisplayId] = React.useState<string>(node.displayId);
  const [uploading, setUploading] = React.useState(false);
  const [cashUploading, setCashUploading] = React.useState(false);
  const { instance, uploadDetails, isSelected } = node;
  const instanceStatus = instance?.status || 'none';
  const [extendedStatus, setExtendedStatus] = React.useState<NodeStatus>(
    instanceStatus,
  );

  // clear state if node changed.
  useEffect(() => {
    if (displayId !== node.displayId) {
      setUploadProgress(null);
      setExtendedStatus(instanceStatus);
      setDisplayId(node.displayId);
      setUploading(false);
    }
    if (!['none', 'initializing'].includes(instanceStatus)) {
      setUploadProgress(null);
      setExtendedStatus(instanceStatus);
      setUploading(false);
    }
  }, [displayId, instanceStatus, node.displayId]);

  const bkColor = COLORS.nodeNone;
  const borderColor = isSelected
    ? COLORS.selectedBorder
    : COLORS.unselectedBorder;

  return (
    <Flex
      w="300px"
      bg={bkColor}
      border={borderColor}
      boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
      borderRadius={SIZES.borderRadius}
      flexDirection="column"
    >
      <NodeHeader node={node} extendedStatus={extendedStatus} />
      <Divider borderColor={COLORS.divider} />
      {uploadDetails.type === 'file' && extendedStatus === 'none' && (
        <Box w="100%" h={SIZES.componentHeight} p={SIZES.padding}>
          <FileUploadButton
            allowedExtensions={[uploadDetails.extension]}
            handleFile={async (file: File) => {
              if (uploading) return;
              try {
                setUploading(true);
                await uploadDetails.upload(
                  file,
                  (loaded: number, total: number) => {
                    setUploadProgress({ loaded, total });
                    setExtendedStatus('upload');
                  },
                );
              } catch (e) {
                const description = commonError.getErrorMessage(e);
                toast({
                  title: 'Error uploading file',
                  description,
                  status: 'error',
                  position: 'top',
                });
              } finally {
                // Note we do not reset the upload progress or extended status here
                // because we want status to stay `upload` until the
                // instance status is updated
                setUploading(false);
              }
            }}
          >
            <Button
              isLoading={uploading}
              leftIcon={<FiUpload />}
              variant="solid"
              colorScheme="aurora"
              width="100%"
            >
              {`Upload ${uploadDetails.extension.toUpperCase()}`}
            </Button>
          </FileUploadButton>
        </Box>
      )}
      {uploadProgress && (
        <Center w="100%" h={SIZES.componentHeight} p={SIZES.padding}>
          <Progress
            colorScheme="green"
            value={(uploadProgress.loaded / uploadProgress.total) * 100}
            size="lg"
            hasStripe={uploadProgress.loaded !== uploadProgress.total}
            w="100%"
          />
        </Center>
      )}

      {uploadDetails.type === 'cashBalance' && extendedStatus === 'none' && (
        <Box w="100%" h={SIZES.componentHeight} p={SIZES.padding}>
          <CashBalanceUploadButton
            organizationId={uploadDetails.organizationId}
            creditFacilityId={uploadDetails.creditFacilityId}
            reportingDate={uploadDetails.reportingDate}
            fileVersion={uploadDetails.fileVersion}
            onUploadStart={() => setCashUploading(true)}
            onUploadEnd={() => setCashUploading(false)}
          >
            <Button
              isLoading={cashUploading}
              leftIcon={<FiUpload />}
              variant="solid"
              colorScheme="aurora"
              width="100%"
            >
              Upload Cash Balance
            </Button>
          </CashBalanceUploadButton>
        </Box>
      )}
    </Flex>
  );
};
